import { Container, Divider } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SkeletonComponent,
  TableAction,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ModalFormParameterAgent from "./Components/ModalFormParameterAgent";

function Agent() {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");

  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    per_page: Number(paramsSize) || 10,
    search: paramsSearch || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [optionComposer, setOptionComposer] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPreload, setModalPreload] = useState(null);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getOptionComposer = async () => {
    try {
      const res = await axios.get(`${hardBaseUrl}/publisher/composer`, {
        headers,
      });
      const modifiedData = res?.data?.data?.map(item => ({
        ...item,
        id: item?.composer_id,
        label: item?.sure_name,
      }));
      setOptionComposer(modifiedData);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    }
  };
  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/publisher/agent`, {
        headers,
        params: queryParams,
      });

      const { data, total_page } = res?.data?.data;
      setDataTable(data);
      setTableTotalPage(total_page);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(async () => {
      setLoadingPage(true);
      await getDataTable();
      setLoadingPage(false);
    }, 500),
    [queryParams]
  );
  const fetchData = async () => {
    setLoadingPage(true);
    const promises = [getDataTable()];
    if (isFirstLoad.current) {
      getOptionComposer();
    }
    try {
      await Promise.all(promises);
    } catch (error) {
      ModalError(error, "Error fetching data");
    } finally {
      isFirstLoad.current = false;
      setLoadingPage(false);
    }
  };
  const handleDelete = async value => {
    try {
      setLoadingPage(true);
      await axios.delete(`${hardBaseUrl}/publisher/agent/${value.agent_id}`, {
        headers,
      });
      ModalSuccess("Data Deleted Successfully").then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      fetchData();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Agent">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <PrimaryButton
              label="Add Agent"
              startIcon={<AddIcon />}
              onClick={() => {
                setModalVisible(true);
                setModalPreload(null);
              }}
            />
          </Grid>
        </Grid>
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.per_page}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "per_page")
            }
            onRequestSort={sortBy => {
              return handleChangeQueryParams(
                `${sortBy} ${
                  queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
                }`,
                "sort"
              );
            }}
            isHaveAction
            renderAction={item => (
              <TableAction
                handleEdit={() => {
                  setModalVisible(true);
                  setModalPreload(item?.agent_id);
                }}
                deleteConfirmation
                deleteConfirmationTitle="Delete Agent"
                deleteConfirmationKey={item?.name}
                handleDelete={() => handleDelete(item)}
              />
            )}
          />
        )}
        {modalVisible && (
          <ModalFormParameterAgent
            open={modalVisible}
            onClose={() => setModalVisible(false)}
            preload={modalPreload}
            getDataTable={getDataTable}
            optionComposer={optionComposer}
          />
        )}
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Parameter",
    link: "/admin/parameter/agent",
  },
  {
    label: "Agent",
    active: true,
  },
];
const columnTable = [
  {
    name: "name",
    title: "Agent Name",
    sort_key: "name",
    useSort: true,
  },
  {
    name: "contact_number",
    title: "Telephone Number",
    sort_key: "contact_number",
    useSort: true,
  },
  {
    name: "email",
    title: "Email",
    sort_key: "email",
    useSort: true,
  },
  {
    name: "npwp",
    title: "NPWP",
    sort_key: "npwp",
    useSort: true,
  },
];

export default Agent;
